import type { SelectProps } from '@bookora/ui';

import { QUARTER_HOUR_OPTIONS, clockOptionMatches } from './clock';

/**
 * The pieces every schedule form in this section shares: the Jalali week in
 * order, the usual working week, and the searchable quarter-hour picker.
 *
 * One copy, because two forms write the same rows — the per-colleague grid
 * (`working-hours-editor.tsx`) and «یک برنامه برای چند کارمند»
 * (`bulk-working-hours-dialog.tsx`) — and a week that started on a different
 * day in each, or a picker that searched Persian digits in one and not the
 * other, is the kind of drift a clinic notices only as "it was right there".
 */

export const WEEKDAYS = [
  { value: 'SATURDAY', label: 'شنبه' },
  { value: 'SUNDAY', label: 'یکشنبه' },
  { value: 'MONDAY', label: 'دوشنبه' },
  { value: 'TUESDAY', label: 'سه‌شنبه' },
  { value: 'WEDNESDAY', label: 'چهارشنبه' },
  { value: 'THURSDAY', label: 'پنجشنبه' },
  { value: 'FRIDAY', label: 'جمعه' },
] as const;

export type Weekday = (typeof WEEKDAYS)[number]['value'];

/** شنبه تا چهارشنبه — the working week most Iranian clinics keep. */
export const DEFAULT_WORK_DAYS: readonly Weekday[] = [
  'SATURDAY',
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
];

export function weekdayLabel(weekday: Weekday): string {
  return WEEKDAYS.find((day) => day.value === weekday)?.label ?? weekday;
}

/** The lunch break the quick builders offer: ۱۳:۰۰ to ۱۴:۰۰. */
export const LUNCH_BREAK = { startMinute: 13 * 60, endMinute: 14 * 60, label: 'ناهار' } as const;

/** Why a quick shift template (days + one start/end + optional lunch) cannot be applied. */
export const QUICK_SHIFT_REASON = {
  NO_DAYS: 'هنوز روزی انتخاب نشده. روزهای کاری رو تیک بزنید.',
  END_BEFORE_START: 'ساعت پایان قبل از شروع است. ساعت پایان رو دیرتر بذارید.',
  LUNCH_OUTSIDE:
    'استراحت ناهار بیرون از این شیفت است. تیک ناهار رو بردارید یا ساعت شیفت رو تغییر بدید.',
} as const;

/** «یک برنامه برای چند کارمند» with nobody ticked yet. */
export const BULK_NO_STAFF = 'هنوز کارمندی انتخاب نشده. حداقل یک کارمند رو تیک بزنید.';

/**
 * The first thing stopping a quick shift template, as «[why]. [what to do].»,
 * or `null`. ONE rule for both quick builders — the per-colleague «ساخت سریع
 * برنامه» and «یک برنامه برای چند کارمند» — so an afternoon shift that cannot
 * hold the lunch break is refused, and explained, the same way in each.
 */
export function quickShiftBlocker({
  days,
  start,
  end,
  lunch,
}: {
  days: readonly Weekday[];
  start: number;
  end: number;
  lunch: boolean;
}): string | null {
  if (days.length === 0) return QUICK_SHIFT_REASON.NO_DAYS;
  if (end <= start) return QUICK_SHIFT_REASON.END_BEFORE_START;
  if (lunch && (start > LUNCH_BREAK.startMinute || end < LUNCH_BREAK.endMinute)) {
    return QUICK_SHIFT_REASON.LUNCH_OUTSIDE;
  }
  return null;
}

/** Searchable, and the search understands Persian digits. See `clock.ts`. */
export const TIME_SELECT_PROPS: Pick<SelectProps<number>, 'options' | 'showSearch' | 'style'> = {
  options: [...QUARTER_HOUR_OPTIONS],
  showSearch: {
    filterOption: (input, option) =>
      typeof option?.value === 'number' ? clockOptionMatches(input, option.value) : false,
  },
  style: { width: '100%' },
};
